import {Dispatch, FC, useState} from 'react';
import dayjs from 'dayjs';
import ListOptions from '../../common/ListOptions';
import {
  dateFormat, dateFormatDDMMYYYY, Subprocess, ProcessActions
} from '../../../constants/constants';
import EnsurePermission from '../../common/EnsurePermission';
import permissionsInfo from '../../../constants/permissionsInfo';
import {Link} from 'react-router-dom';
import LiveTimeProgressBar
  from '../../common/time_progress_bar/LiveTimeProgressBar';
import {AlertSettingsProps} from '../../settings/alert_alarm/types';
import ProcessExpiredTime from '../ProcessExpiredTime';
import processesRoutes from '../routes';
import {calculateAndFormatTimeMMSS} from '../helpers';
import {useTranslation} from 'react-i18next';
import {ProgressBarState} from '../../../constants/timeProgressBar';
import {ProcessFilter} from '../types';
import {RunningProcess} from '../../../constants/types';
import ProcessClaimingButton from './ProcessClaimingButton';

type RunningProcessesElementProps = {
  data: RunningProcess,
  setError: Dispatch<string>,
  options: string[],
  handleShowSubprocessPopup: (
    id: number, subprocessType: Subprocess,
    taskName: string, patientName: string
  ) => void,
  handleShowProcessActionsPopup: (
    id: number, processActionsType: ProcessActions,
    confirmBtnLabel: string, assigneeId: number) => void,
  processAlertData: Nullable<AlertSettingsProps>,
  filter: Nullable<ProcessFilter>,
  currentUserId: Nullable<number>,
  hasProcessClaimButton: boolean;
}

export const RunningProcessesElement: FC<RunningProcessesElementProps> = ({
  data,
  handleShowSubprocessPopup,
  handleShowProcessActionsPopup,
  processAlertData,
  filter,
  options,
  currentUserId,
  hasProcessClaimButton
}) => {
  const {t} = useTranslation('processes');
  const currentTime = dayjs();
  const [processState, setProcessState] =
    useState<Nullable<ProgressBarState>>(null);

  const {
    patient,
    startedSubProcesses,
    id,
    type,
    callSheet: {appointment},
    startedAt,
    completedAt,
    targetTime,
    assignees
  } = data;
  const patientName = `${patient.firstName} ${patient.lastName}`;

  const earlyArrivalValidation = (date) => {
    if (!date) return false;

    return dayjs(date).diff(currentTime, 'day') > 0 ||
      currentTime.format(dateFormatDDMMYYYY) ===
      dayjs(date).format(dateFormatDDMMYYYY);
  };

  const subprocessTooltipMessage = (subprocess, condition) => {
    const subprocessCode = subprocess;
    const subprocessName = t(`subprocessNames.${subprocessCode}`);

    return startedSubProcesses.includes(subprocessCode) ?
    t('startedSubprocessTooltipMessage', {subprocessName}) : condition;
  };

  const isSubProcessDisabled =
  startedSubProcesses.includes(Subprocess.EARLY_ARRIVAL) ||
  startedSubProcesses.includes(Subprocess.RESCHEDULE_APPOINTMENT) ||
  !earlyArrivalValidation(appointment?.appointmentDate)||
  !appointment?.appointmentDate;

  const subProcesses = {
    TERMINATE: {
      name: 'Terminate',
      permission: permissionsInfo.processes.terminate,
      onClick: () => handleShowSubprocessPopup(
          id, Subprocess.TERMINATE, type.description || '', patientName
      )
    },
    EARLY_ARRIVAL: {
      name: 'Early Arrival',
      permission: permissionsInfo.processes.early_arrival,
      tooltipMessage:
        subprocessTooltipMessage(
            Subprocess.EARLY_ARRIVAL,
            !earlyArrivalValidation(appointment?.appointmentDate) && t('earlyArrivalTooltipMessage')
        ),
      disabled: isSubProcessDisabled,
      onClick: () => handleShowSubprocessPopup(
          id, Subprocess.EARLY_ARRIVAL, type.description || '', patientName
      )
    },
    RESCHEDULE_APPOINTMENT: {
      name: 'Reschedule / Cancel',
      permission: permissionsInfo.processes.reschedule_cancel,
      tooltipMessage:
        subprocessTooltipMessage(
            Subprocess.RESCHEDULE_APPOINTMENT,
            !appointment?.appointmentDate && t('rescheduleAppointmentTooltipMessage')
        ),
      disabled: isSubProcessDisabled,
      onClick: () => handleShowSubprocessPopup(
          id, Subprocess.RESCHEDULE_APPOINTMENT, type.description || '', patientName
      )
    }
  };

  const subProcessesOptions = options.reduce((obj, key) =>
    ({...obj, [key]: subProcesses[key]}), {});

  const subProcessesPermissions = Object.keys(subProcessesOptions).map(
      (key) => subProcessesOptions?.[key]?.permission).flat(1);

  const formattedStartAt = dayjs(startedAt).format(dateFormat);

  if (filter) {
    if (filter === ProcessFilter.AHEAD &&
      (processState === ProgressBarState.EXPIRED ||
        processState === ProgressBarState.ALERT)) {
      return null;
    }

    if (filter === ProcessFilter.BEHIND &&
      processState === ProgressBarState.NORMAL) {
      return null;
    }
  }

  return (
    <div className="running-processes-element">
      <div className="running-processes-element_name-wrapper">
        <Link
          to={processesRoutes.runningItem(id)}
          className="running-processes-element_name"
        >
          {patient && patientName}
          {formattedStartAt && ` ${formattedStartAt}`}
        </Link>
      </div>
      <ProcessExpiredTime
        startedAt={startedAt}
        completedAt={completedAt}
        targetTime={targetTime}
        alertSettings={processAlertData}
        timeFormat={calculateAndFormatTimeMMSS}
        setProcessState={setProcessState}
      />
      <LiveTimeProgressBar
        startedAt={startedAt}
        completedAt={completedAt}
        targetTime={targetTime}
        alertSettings={processAlertData}
      />
      <EnsurePermission elementPermissions={permissionsInfo.tasks.claim}>
        {hasProcessClaimButton && (
          <ProcessClaimingButton
            id={id}
            assignees={assignees}
            onButtonClick={handleShowProcessActionsPopup}
            currentUserId={currentUserId}
          />
        )}
      </EnsurePermission>
      <EnsurePermission elementPermissions={subProcessesPermissions}>
        <ListOptions options={subProcessesOptions} />
      </EnsurePermission>
    </div>
  );
};

export default RunningProcessesElement;
